import { BellRing, Clock, Hash, Users } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { WeekEditor, type WeekWords } from "@/features/calendar-sections";
import { ApiError } from "@/lib/api";
import { allDay, officeHours, timezoneOptions } from "@/lib/calendars";
import { useSaveStaffingAlert, useStaffingAlert } from "@/lib/queries";
import { useSlackChannels, useSlackConnections, type SlackConnection } from "@/lib/slack";
import {
  WAIT_CHOICES, alertSummary, allTheTime, draftProblem, draftSentence, slackPreview, slackProblemText,
  startingDraft, toSave, waitText, zoneLabel,
} from "@/lib/staffing";
import type { AvailabilityRange, Queue, StaffingAlertInput } from "@/lib/types";
import { cn } from "@/lib/utils";

const LINE_WEEK: WeekWords = {
  custom: "Working hours",
  customOn: "Only in the hours below.",
  customOff: "All day, every day.",
  closed: "No one needed",
  zone: "the line's clock",
};

function usable(c: SlackConnection): boolean {
  return c.enabled && c.status === "ok";
}

/** The line's alert in a few words, under its name in the Lines table. Nothing while it is off. */
export function StaffingAlertSummary({ line }: { line: Queue }) {
  const alert = useStaffingAlert(line.id);
  if (!alert.data?.enabled) return null;
  const summary = alertSummary(alert.data);
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-xs text-text-muted" title={`Nobody Ready alert: ${summary}`}>
      <BellRing className="size-3 shrink-0" aria-hidden />
      <span className="truncate">{summary}</span>
    </span>
  );
}

/** A numbered part of the form: a question, and what answers it. */
function Part({
  n, icon: Icon, title, hint, children,
}: {
  n: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-lg border border-border p-4" aria-label={title}>
      <div className="flex items-start gap-3">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-3 text-xs font-semibold text-accent-11">
          {n}
        </span>
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-text">
            <Icon className="size-4 text-text-muted" aria-hidden />
            {title}
          </h3>
          {hint && <p className="mt-0.5 text-xs text-text-muted">{hint}</p>}
        </div>
      </div>
      <div className="space-y-3 pl-9">{children}</div>
    </section>
  );
}

/** Two or more cards to pick one of, the line form's hold music choice in another place. */
function Choices<T extends string>({
  name, value, options, onChange,
}: {
  name: string;
  value: T;
  options: readonly { key: T; label: string; description: string }[];
  onChange: (key: T) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((o) => (
        <label
          key={o.key}
          className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2"
        >
          <input
            type="radio"
            name={name}
            className="mt-0.5 accent-accent-9"
            checked={value === o.key}
            onChange={() => onChange(o.key)}
          />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-text">{o.label}</span>
            <span className="block text-xs text-text-muted">{o.description}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

/**
 * Who is told when the line has nobody to take its next call inside its working hours (ACL-567):
 * always the admins and supervisors in their notifications, and a Slack channel when one is chosen.
 * Three questions, in order: when the line should have people, how long it may sit empty, and who
 * is told. Saved whole, beside the line's routing, which it never changes.
 */
export function StaffingAlertDialog({
  line, open, onOpenChange,
}: {
  line: Queue | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const saved = useStaffingAlert(line?.id ?? null, { enabled: open });
  const save = useSaveStaffingAlert();
  const connections = useSlackConnections(open);
  const [draft, setDraft] = React.useState<StaffingAlertInput | null>(null);
  const [touched, setTouched] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const channels = useSlackChannels(draft?.slack_connection_id ?? null, open);

  React.useEffect(() => {
    if (!open) {
      setDraft(null);
      setTouched(false);
      setError(null);
    } else if (saved.data && draft === null) {
      setDraft(startingDraft(saved.data));
    }
  }, [open, saved.data, draft]);

  const change = (next: Partial<StaffingAlertInput>) => {
    setDraft((d) => (d ? { ...d, ...next } : d));
    setTouched(true);
  };
  const setHours: React.Dispatch<React.SetStateAction<AvailabilityRange[]>> = (next) => {
    setDraft((d) => (d ? { ...d, hours: typeof next === "function" ? next(d.hours) : next } : d));
    setTouched(true);
  };
  const problem = draft ? draftProblem(draft) : null;
  const list = connections.data ?? [];
  const chosen = list.find((c) => c.id === draft?.slack_connection_id);
  const slackOn = draft?.slack_connection_id !== null && draft?.slack_connection_id !== undefined;
  const status = saved.data?.status;
  const title = line ? (line.campaign_name ? `${line.campaign_name} · ${line.name}` : line.name) : "";

  const turnSlack = (on: boolean) => {
    if (!on) {
      change({ slack_connection_id: null, slack_channel_id: null, slack_channel_name: null });
      return;
    }
    // One workspace is the usual case: chosen for the admin, who then only picks the channel.
    const first = list.find(usable) ?? list[0];
    change({ slack_connection_id: first?.id ?? null, slack_channel_id: null, slack_channel_name: null });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!line || !draft || problem) return;
    setError(null);
    try {
      const result = await save.mutateAsync({ queueId: line.id, ...toSave(draft) });
      toast.success(result.enabled ? "Alert on" : "Alert off", `${line.name}: ${alertSummary(result)}`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the alert.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Wide, so a day's start and end sit side by side in the week editor. */}
      <DialogContent variant="drawer" size="lg">
        <DiscardGuard changed={touched} />
        <DialogHeader>
          <DialogTitle>Nobody Ready alert</DialogTitle>
          <DialogDescription>
            For <span className="font-mono">{line?.name}</span>. Get told when nobody on this line can
            take a call: everyone is on a break, signed out, or has their phone disconnected. A rep
            on a call does not count as missing.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {saved.error ? (
              <Callout tone="danger">
                {saved.error instanceof ApiError ? saved.error.message : "Could not load the alert."}
              </Callout>
            ) : !draft ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : (
              <>
                {error && <Callout tone="danger">{error}</Callout>}
                {status?.problem && slackOn && (
                  <Callout tone="warning">
                    The last Slack message did not go out: {slackProblemText(status.problem)}.
                  </Callout>
                )}

                <label className="flex items-center justify-between gap-4 rounded-lg border border-border bg-gray-2 px-4 py-3">
                  <span>
                    <span className="block text-sm font-semibold text-text">Send this alert</span>
                    <span className="block text-xs text-text-muted">
                      {draft.enabled
                        ? "On. Set when and who below, then save."
                        : "Off. Nobody is told when this line is empty. Turn it on to set it up."}
                    </span>
                  </span>
                  <Switch
                    checked={draft.enabled}
                    aria-label="Send this alert"
                    onCheckedChange={(enabled) => change({ enabled })}
                  />
                </label>

                {/* The questions only while it is on: off, there is nothing to set but the switch. What
                    was set stays in the draft, so turning it back on brings it back. */}
                {draft.enabled && (
                  <div className="space-y-4">
                    <Part
                      n={1}
                      icon={Clock}
                      title="When should this line have people?"
                      hint="Outside these hours the line may be empty, and nobody is told."
                    >
                      <Choices
                        name="staffing-hours"
                        value={allTheTime(draft.hours) ? "always" : "set"}
                        options={[
                          { key: "always", label: "All day, every day", description: "The line should never be empty." },
                          { key: "set", label: "Only at set times", description: "Pick the days and hours below." },
                        ]}
                        onChange={(key) => setHours(key === "always" ? allDay() : officeHours())}
                      />
                      {!allTheTime(draft.hours) && (
                        <>
                          <Field label="Timezone" htmlFor="staffing-zone" hint="The hours below are on this clock.">
                            <Select value={draft.timezone} onValueChange={(timezone) => change({ timezone })}>
                              <SelectTrigger id="staffing-zone" className="w-72">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {timezoneOptions(draft.timezone).map((z) => (
                                  <SelectItem key={z} value={z}>
                                    {zoneLabel(z)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                          <WeekEditor
                            draft={draft.hours}
                            setDraft={setHours}
                            clock={{ timezone: draft.timezone, format: "12h", weekStartsOn: "mon" }}
                            words={LINE_WEEK}
                            fixed
                          />
                        </>
                      )}
                    </Part>

                    <Part
                      n={2}
                      icon={Users}
                      title="How long can it be empty before the alert?"
                      hint="If a caller is already waiting, the alert goes out right away, whatever you pick here."
                    >
                      <Field label="Alert after the line is empty for" htmlFor="staffing-wait">
                        <Select value={String(draft.wait_seconds)} onValueChange={(v) => change({ wait_seconds: Number(v) })}>
                          <SelectTrigger id="staffing-wait" className="w-60">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {!WAIT_CHOICES.some((c) => c.seconds === draft.wait_seconds) && (
                              <SelectItem value={String(draft.wait_seconds)}>{waitText(draft.wait_seconds)}</SelectItem>
                            )}
                            {WAIT_CHOICES.map((c) => (
                              <SelectItem key={c.seconds} value={String(c.seconds)}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </Part>

                    <Part
                      n={3}
                      icon={BellRing}
                      title="Who gets the alert?"
                      hint="While the line stays empty, Slack is reminded every 5 minutes, and told when a rep is Ready again."
                    >
                      <p className="flex items-center gap-2 rounded-md bg-gray-2 px-3 py-2 text-sm text-text">
                        <BellRing className="size-4 shrink-0 text-text-muted" aria-hidden />
                        Admins and supervisors, in their notifications. Always.
                      </p>

                      <label className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                        <span className="flex items-center gap-2 text-sm font-medium text-text">
                          <Hash className="size-4 text-text-muted" aria-hidden />
                          Also post in a Slack channel
                        </span>
                        <Switch
                          checked={slackOn}
                          aria-label="Also post in a Slack channel"
                          disabled={list.length === 0}
                          onCheckedChange={turnSlack}
                        />
                      </label>

                      {connections.data && list.length === 0 && (
                        <Callout tone="info">
                          No Slack workspace is connected yet. Connect one in{" "}
                          <Link to="/slack" className="font-medium underline underline-offset-4">
                            Settings → Slack
                          </Link>
                          , then come back to choose its channel.
                        </Callout>
                      )}

                      {slackOn && (
                        <>
                          {(list.length > 1 || !chosen) && (
                            <Field label="Slack workspace" htmlFor="staffing-slack">
                              <Select
                                value={String(draft.slack_connection_id)}
                                onValueChange={(v) =>
                                  change({ slack_connection_id: Number(v), slack_channel_id: null, slack_channel_name: null })
                                }
                              >
                                <SelectTrigger id="staffing-slack" aria-label="Slack workspace">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {connections.data && !chosen && (
                                    <SelectItem value={String(draft.slack_connection_id)} disabled>
                                      {saved.data?.slack_connection_name ?? "A connection you no longer have"}
                                    </SelectItem>
                                  )}
                                  {list.map((c) => (
                                    <SelectItem key={c.id} value={String(c.id)}>
                                      {c.name}
                                      {c.team_name && c.team_name !== c.name ? ` · ${c.team_name}` : ""}
                                      {usable(c) ? "" : " (needs attention)"}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </Field>
                          )}
                          {chosen && !usable(chosen) && (
                            <Callout tone="warning">
                              This Slack connection is turned off, or Slack refused its token. Fix it in{" "}
                              <Link to="/slack" className="font-medium underline underline-offset-4">
                                Settings → Slack
                              </Link>
                              .
                            </Callout>
                          )}
                          <Field
                            label="Channel"
                            htmlFor="staffing-channel"
                            hint={list.length === 1 && chosen ? `In ${chosen.name}. Invite the app to a private channel first.` : "Invite the app to a private channel first."}
                          >
                            <Select
                              value={draft.slack_channel_id ?? ""}
                              onValueChange={(id) =>
                                change({
                                  slack_channel_id: id,
                                  slack_channel_name: channels.data?.find((c) => c.id === id)?.name ?? null,
                                })
                              }
                            >
                              <SelectTrigger id="staffing-channel" aria-label="Channel">
                                <SelectValue placeholder={channels.isFetching ? "Loading channels…" : "Choose a channel"} />
                              </SelectTrigger>
                              <SelectContent>
                                {draft.slack_channel_id && !channels.data?.some((c) => c.id === draft.slack_channel_id) && (
                                  <SelectItem value={draft.slack_channel_id} disabled>
                                    #{draft.slack_channel_name ?? draft.slack_channel_id}
                                  </SelectItem>
                                )}
                                {channels.data?.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    #{c.name}
                                    {c.is_private ? " (private)" : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                          {channels.error && (
                            <Callout tone="danger">
                              {channels.error.message}{" "}
                              <Button type="button" variant="ghost" size="sm" onClick={() => void channels.refetch()}>
                                Try again
                              </Button>
                            </Callout>
                          )}
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-text-muted">What Slack shows (an example)</p>
                            <div className="rounded-md border border-border bg-gray-2 px-3 py-2 text-sm text-text">
                              {slackPreview(title, draft.wait_seconds).map((text, i) => (
                                <p key={i} className={cn(i === 0 && "font-semibold")}>
                                  {i === 0 && <span className="mr-1 rounded bg-warning-3 px-1 text-warning-11">@here</span>}
                                  {text}
                                </p>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </Part>
                  </div>
                )}
              </>
            )}
          </DialogBody>
          <DialogFooter className="sm:items-center">
            {draft && (
              <p
                className={cn("min-w-0 text-xs sm:mr-auto", problem ? "text-danger-11" : "text-text-muted")}
                role={problem ? "alert" : undefined}
                aria-live="polite"
              >
                {problem ?? draftSentence(draft, line?.name ?? "")}
              </p>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={!draft || problem !== null} loading={save.isPending}>
              Save alert
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
